import React, { createContext, useState, useEffect } from "react";
import axios from "axios";
import { clearShippingCache } from "../services/shipping";

// Create context
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // Generate guest token.
  // Note: there is no backend guest-token endpoint — the guest token is just a
  // client-side identifier the API echoes back via the x-guest-token header
  // (optionalAuth does not JWT-verify it). So we create it locally instead of
  // calling a non-existent /api/guest-token route (which only ever 404'd).
  const generateGuestToken = async () => {
    const guestToken = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setToken(guestToken);
    setIsAuthenticated(false);
    localStorage.setItem("authToken", guestToken);
    localStorage.setItem("isGuest", "true");
    return guestToken;
  };

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        const storedUser = localStorage.getItem("userInfo");
        const storedToken = localStorage.getItem("authToken");
        const isGuest = localStorage.getItem("isGuest") === "true";

        if (storedUser && storedToken && !isGuest) {
          // User is authenticated
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setToken(storedToken);
          setIsAuthenticated(true);
        } else if (storedToken && isGuest) {
          // Guest user with existing token
          setToken(storedToken);
          setIsAuthenticated(false);
        } else {
          // No token exists, generate guest token
          await generateGuestToken();
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        // Fallback to guest token generation
        await generateGuestToken();
      } finally {
        setIsLoading(false);
        setAuthReady(true);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (userData, authToken) => {
    try {
      setUser(userData);
      setToken(authToken);
      setIsAuthenticated(true);
      
      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem("authToken", authToken);
      localStorage.setItem("isGuest", "false");

      // First-order free delivery is decided per account — drop the cached
      // answer so it's re-checked for whoever just signed in.
      clearShippingCache();

      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setUser(null);
      setIsAuthenticated(false);
      
      // Clear user data but keep guest token generation for after
      localStorage.removeItem("userInfo");
      localStorage.removeItem("authToken");
      localStorage.removeItem("isGuest");

      clearShippingCache();

      // Generate new guest token
      await generateGuestToken();
      
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      return false;
    }
  };

  // Check if token is valid (you might want to implement this)
  const validateToken = async (tokenToValidate) => {
    try {
      const response = await axios.get("/api/validate-token", {
        headers: {
          Authorization: `Bearer ${tokenToValidate}`
        }
      });
      return response.data.valid;
    } catch (error) {
      console.error("Token validation error:", error);
      return false;
    }
  };

  // Refresh token if needed
  const refreshToken = async () => {
    try {
      const response = await axios.post("/api/refresh-token", {
        token: token
      });
      
      const newToken = response.data.token;
      setToken(newToken);
      localStorage.setItem("authToken", newToken);
      
      return newToken;
    } catch (error) {
      console.error("Token refresh error:", error);
      // If refresh fails and user was authenticated, logout
      if (isAuthenticated) {
        logout();
      }
      return null;
    }
  };

  // Get auth headers for API requests
  const getAuthHeaders = () => {
    console.log("getAuthHeader "+ token)
    if (token) {
      return {
        Authorization: `Bearer ${token}`
      };
    }
    return {};
  };

  // Check if user is guest
  const isGuest = () => {
    return !isAuthenticated && token !== null;
  };

  const contextValue = {
    user,
    token,
    isAuthenticated,
    isLoading,
    authReady,
    login,
    logout,
    generateGuestToken,
    validateToken,
    refreshToken,
    getAuthHeaders,
    isGuest,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};